import { motion } from "framer-motion";
import { ShieldCheck, Ship } from "lucide-react";
import { Reveal, SectionHead, EASE } from "./Reveal";
import { useLang } from "../i18n";

const RouteArc = ({ d, delay }) => (
  <motion.path
    d={d}
    fill="none"
    stroke="#D4A017"
    strokeWidth="1.6"
    strokeDasharray="5 7"
    strokeLinecap="round"
    initial={{ pathLength: 0, opacity: 0 }}
    whileInView={{ pathLength: 1, opacity: 0.9 }}
    viewport={{ once: true }}
    transition={{ duration: 1.8, delay, ease: EASE }}
  />
);

const Port = ({ cx, cy, label, delay, align = "middle" }) => (
  <motion.g
    initial={{ opacity: 0, scale: 0.6 }}
    whileInView={{ opacity: 1, scale: 1 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6, delay, ease: EASE }}
  >
    <circle cx={cx} cy={cy} r="5" fill="#D4A017" />
    <circle cx={cx} cy={cy} r="11" fill="none" stroke="#D4A017" strokeOpacity="0.4" strokeWidth="1">
      <animate attributeName="r" values="8;16" dur="2.2s" repeatCount="indefinite" />
      <animate attributeName="stroke-opacity" values="0.5;0" dur="2.2s" repeatCount="indefinite" />
    </circle>
    <text x={cx} y={cy - 18} textAnchor={align} fill="#FDFBF7" fontSize="12" fontFamily="Manrope" fontWeight="600" letterSpacing="2">
      {label}
    </text>
  </motion.g>
);

const RoutesMap = () => (
  <svg viewBox="0 0 1000 460" className="h-auto w-full" role="img" aria-label="Carte des routes commerciales ORIS">
    {Array.from({ length: 11 }, (_, i) => (
      <line key={`v${i}`} x1={i * 100} y1="0" x2={i * 100} y2="460" stroke="#FDFBF7" strokeOpacity="0.05" />
    ))}
    {Array.from({ length: 6 }, (_, i) => (
      <line key={`h${i}`} x1="0" y1={i * 92} x2="1000" y2={i * 92} stroke="#FDFBF7" strokeOpacity="0.05" />
    ))}
    <RouteArc d="M 330 250 Q 420 60 560 130" delay={0.2} />
    <RouteArc d="M 330 250 Q 560 320 790 200" delay={0.5} />
    <RouteArc d="M 330 250 Q 160 120 130 170" delay={0.8} />
    <RouteArc d="M 560 130 Q 700 80 790 200" delay={1.1} />
    <Port cx={330} cy={250} label="ABIDJAN" delay={0.1} />
    <Port cx={560} cy={130} label="ROTTERDAM" delay={0.4} />
    <Port cx={790} cy={200} label="HÔ CHI MINH" delay={0.7} />
    <Port cx={130} cy={170} label="NEW YORK" delay={1.0} />
  </svg>
);

export const Logistics = () => {
  const { t } = useLang();
  return (
    <section id="logistique" data-testid="logistics-section" className="grain relative bg-oris-green py-24 lg:py-36">
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead overline={t.trade.overline} title={t.trade.title} tone="dark" testId="logistics-title" />
        <Reveal>
          <p className="-mt-6 mb-16 max-w-2xl text-base leading-relaxed text-oris-cream/75 lg:text-lg">{t.trade.intro}</p>
        </Reveal>

        <Reveal>
          <div data-testid="trade-routes-map" className="border border-oris-cream/12 bg-oris-deep/50 p-4 sm:p-10">
            <RoutesMap />
          </div>
        </Reveal>

        <div className="mt-14 grid gap-10 lg:grid-cols-3">
          <Reveal>
            <div data-testid="trade-zones-block">
              <h3 className="mb-6 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.24em] text-oris-gold">
                <Ship className="h-4 w-4" /> {t.trade.zonesTitle}
              </h3>
              <ul className="divide-y divide-oris-cream/10 border-y border-oris-cream/10">
                {t.trade.zones.map((z) => (
                  <li key={z.name} className="flex items-baseline justify-between gap-4 py-4">
                    <span className="font-serif text-xl text-oris-cream">{z.name}</span>
                    <span className="text-right text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-oris-cream/55">{z.role}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div data-testid="certifications-block">
              <h3 className="mb-6 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.24em] text-oris-gold">
                <ShieldCheck className="h-4 w-4" /> {t.trade.certsTitle}
              </h3>
              <div className="flex flex-wrap gap-3">
                {t.trade.certs.map((c) => (
                  <span
                    key={c}
                    className="border border-oris-cream/20 px-4 py-2.5 text-[0.72rem] font-semibold uppercase tracking-[0.14em] text-oris-cream/85 transition-colors duration-300 hover:border-oris-gold hover:text-oris-gold"
                  >
                    {c}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <div data-testid="fleet-block" className="border border-oris-cream/12 bg-oris-deep/40 p-7">
              <h3 className="font-serif text-2xl font-medium text-oris-cream">{t.trade.fleetTitle}</h3>
              <p className="mt-4 text-sm leading-relaxed text-oris-cream/70">{t.trade.fleetText}</p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
